import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { UploadCloud, Loader2, CheckCircle2, X } from 'lucide-react';
import { useApi } from '@/hooks/use-api';
import { useAuth } from '@/hooks/use-auth';
import { Button } from '@/components/ui/button';

const CHUNK = 8 * 1024 * 1024; // 8MB per part (R2 min part size is 5MB)

/**
 * Backend-proxied multipart upload (browser → /api/uploads → R2). Same-origin, so
 * no bucket CORS config is required. Calls onUploaded with
 * { key, filename, content_type, size }. `accept` is an input accept string.
 */
export const FileUpload = ({ folder = 'lms', accept, label = 'Upload file', currentName, onUploaded, onClear, resetAfterUpload = false, transform, testid, maxSizeMB }) => {
  const api = useApi();
  const { token, API } = useAuth();
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null); // null | 0-100
  const [name, setName] = useState(currentName || '');

  const putPart = (key, uploadId, partNumber, blob, onProg) => new Promise((resolve, reject) => {
    const qs = `key=${encodeURIComponent(key)}&upload_id=${encodeURIComponent(uploadId)}&part_number=${partNumber}`;
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', `${API}/api/uploads/multipart/part?${qs}`, true);
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProg(e.loaded); };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(JSON.parse(xhr.responseText)); } catch { reject(new Error('Bad response')); }
      } else reject(new Error(`Chunk failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.send(blob);
  });

  const upload = async (file) => {
    const content_type = file.type || 'application/octet-stream';
    const { key, upload_id } = await api.post('/api/uploads/multipart/start', { filename: file.name, content_type, folder });
    const totalParts = Math.max(1, Math.ceil(file.size / CHUNK));
    const parts = [];
    let uploadedBase = 0;
    for (let i = 0; i < totalParts; i++) {
      const start = i * CHUNK;
      const blob = file.slice(start, Math.min(start + CHUNK, file.size));
      // eslint-disable-next-line no-await-in-loop
      const res = await putPart(key, upload_id, i + 1, blob, (loaded) => {
        setProgress(Math.min(99, Math.round(((uploadedBase + loaded) / file.size) * 100)));
      });
      parts.push({ PartNumber: res.PartNumber, ETag: res.ETag });
      uploadedBase += blob.size;
      setProgress(Math.min(99, Math.round((uploadedBase / file.size) * 100)));
    }
    const doc = await api.post('/api/uploads/multipart/complete', { key, upload_id, parts, filename: file.name, content_type, size: file.size, folder });
    setProgress(100);
    return doc;
  };

  const onFile = async (e) => {
    let file = e.target.files?.[0];
    if (!file) return;
    if (maxSizeMB && file.size > maxSizeMB * 1024 * 1024) {
      toast.error(`File too large`, { description: `Max ${maxSizeMB}MB — this file is ${(file.size / 1024 / 1024).toFixed(1)}MB.` });
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    if (transform) {
      try { file = await transform(file); } catch { file = null; }
      if (!file) { if (inputRef.current) inputRef.current.value = ''; return; }
    }
    setProgress(0);
    try {
      const doc = await upload(file);
      onUploaded?.({ key: doc.key, filename: file.name, content_type: doc.content_type, size: file.size });
      toast.success('File uploaded');
      setName(resetAfterUpload ? '' : file.name);
    } catch (err) { toast.error('Upload failed', { description: err.message }); }
    finally { setProgress(null); if (inputRef.current) inputRef.current.value = ''; }
  };

  return (
    <div>
      <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={onFile} data-testid={testid ? `${testid}-input` : undefined} />
      {name ? (
        <div className="flex items-center gap-2 rounded-lg border border-success/30 bg-success/5 px-3 py-2 text-sm" data-testid={testid ? `${testid}-uploaded` : undefined}>
          <CheckCircle2 className="h-4 w-4 text-success shrink-0" />
          <span className="truncate flex-1">{name}</span>
          <button type="button" onClick={() => { setName(''); onClear?.(); }} className="text-muted-foreground hover:text-destructive"><X className="h-3.5 w-3.5" /></button>
        </div>
      ) : progress !== null ? (
        <div className="rounded-lg border border-border bg-card px-3 py-2">
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Uploading… {progress}%</div>
          <div className="mt-1.5 h-1.5 rounded-full bg-secondary overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} /></div>
        </div>
      ) : (
        <Button type="button" variant="outline" className="w-full gap-2 border-dashed h-10" onClick={() => inputRef.current?.click()} data-testid={testid ? `${testid}-btn` : undefined}>
          <UploadCloud className="h-4 w-4" /> {label}
        </Button>
      )}
    </div>
  );
};

export default FileUpload;
